import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useO5AwsDeployerV1StackQueryServiceStackGet } from '@interxfi/api-client/generated/hooks/o5-aws-deployer-v1-stack-query-service-stack-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { getShortenedId } from '@interxfi/design-system/components/format/id/shared';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import { Typography } from '@interxfi/design-system/components/typography';
import type { O5AwsDeployerV1StackState } from '@interxfi/types/generated/api';
import { O5StackDeploymentsTable } from '../../../../../../../features/o5/stack/o5-stack-deployments-table';
import { O5StackEntityMenu } from '../../../../../../../features/o5/stack/o5-stack-entity-menu';
import { O5StackEventSummary } from '../../../../../../../features/o5/stack/o5-stack-event';
import { getO5StackStateListItems } from '../../../../../../../features/o5/stack/o5-stack-list-items';
import { getO5StackDetailPath } from '../../../../../../../features/o5/stack/o5-stack-routing';
import { O5StackStatusPill } from '../../../../../../../features/o5/stack/o5-stack-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../util/routing/meta';

function StackBreadcrumb({ stackId }: { stackId: string }) {
  const { data, error, isPending } = useO5AwsDeployerV1StackQueryServiceStackGet({ stackId });
  const showPending = Boolean(isPending || error);

  if (showPending) {
    return <Skeleton charCount={8} />;
  }

  return data?.stack?.data?.stackName || `Stack ${getShortenedId(stackId)}`;
}

export const Route = createFileRoute('/_app/_o5/_o5-required/tech-ops/stack/_stack/$stackId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `stack-${params.stackId}`,
        label: <StackBreadcrumb stackId={params.stackId} />,
        to: getO5StackDetailPath(params.stackId),
        suffix: <CopyButton content={params.stackId} label="Copy Stack ID" tooltip={{ copy: params.stackId, copied: 'Copied Stack ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Stack'),
  component: O5StackDetail,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load stack', 'view this stack');

function getListItems(data: O5AwsDeployerV1StackState | undefined) {
  const items = getO5StackStateListItems(data);

  return {
    left: [
      {
        id: 'main',
        items: [items.data.stackName, items.data.applicationName, items.data.environmentName],
      },
      ...(items.data.currentDeployment
        ? [
            {
              id: 'currentDeployment',
              title: 'Current Deployment',
              items: [items.data.currentDeployment.deploymentId, items.data.currentDeployment.version],
            },
          ]
        : []),
    ] as const satisfies ReadonlyListGroup[],
    right: [
      items.metadata.createdAt,
      items.metadata.updatedAt,
      items.stackId,
      items.environmentId,
      items.clusterId,
    ] as const satisfies ReadonlyListItem[],
  } as const;
}

function O5StackDetail() {
  const { stackId } = Route.useParams();
  const request = { stackId } as const;
  const { data, error, isPending } = useO5AwsDeployerV1StackQueryServiceStackGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.stack), [data?.stack]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <O5StackStatusPill isPending={showPending} size="large" status={data?.stack?.status} />
          </HorizontalStack>
          <O5StackEntityMenu stackId={stackId} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <O5StackEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Queued Deployments</Typography.Level3>
          <O5StackDeploymentsTable
            deployments={data?.stack?.data?.queuedDeployments || []}
            renderWhenEmpty="No Deployments Queued"
            showSkeleton={showPending}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
